
import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Calendar, MapPin } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLeadCapture } from "@/hooks/useLeadCapture";
import TocFotosFooter from "@/components/TocFotosFooter";
import { isBrowser } from "@/utils/runtime";

interface EventCardProps {
  title: string;
  photographer: string;
  date: string;
  location: string;
  imageUrl: string;
}

const EventCard: React.FC<EventCardProps> = ({ title, photographer, date, location, imageUrl }) => (
  <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all">
    <div 
      className="w-full h-48 bg-cover bg-center"
      style={{ backgroundImage: `url(${imageUrl})` }}
    />
    <div className="p-4">
      <h3 className="font-bold text-lg mb-1 text-[#323031]">{title}</h3>
      <p className="text-gray-700 text-sm mb-2">Fotógrafo: {photographer}</p>
      <div className="flex items-center text-sm text-gray-600 mb-1">
        <Calendar size={16} className="text-[#3322FC] mr-2" />
        <span>{date}</span>
      </div>
      <div className="flex items-center text-sm text-gray-600">
        <MapPin size={16} className="text-[#3322FC] mr-2" />
        <span>{location}</span>
      </div>
    </div>
  </div>
);

const TocFotosEventos02 = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  
  // Set page title for SEO
  useEffect(() => {
    if (!isBrowser) return;
    globalThis.document.title = "Buscar Fotos | Toc Fotos Eventos";

    // Add meta description
    const metaDescription = globalThis.document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content",
        "Reviva o seu melhor momento. Encontre e compre suas fotos de eventos esportivos e culturais em todo o Brasil.");
    }
  }, []);
  
  const { openLeadCapture, LeadCaptureModalComponent } = useLeadCapture({
    title: "Seja um fotógrafo parceiro",
    description: "Preencha seus dados para começar a vender suas fotos",
    onSuccess: () => {
      if (isBrowser) {
        globalThis.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  });

  // Sample event data
  const events = [
    {
      title: "Copa Rio de Beach Tennis",
      photographer: "Carlos Mendes",
      date: "15/05/2025",
      location: "Praia de Copacabana, Rio de Janeiro",
      imageUrl: "/lovable-uploads/5d0bfcd5-8f46-490c-8e19-f0153b1a59b3.png",
      category: "beach-tennis"
    },
    {
      title: "Maratona de São Paulo",
      photographer: "Ana Silva",
      date: "10/05/2025",
      location: "Parque Ibirapuera, São Paulo",
      imageUrl: "/lovable-uploads/e4405c79-3173-4e9c-81d5-25973e61413d.png",
      category: "corrida"
    },
    {
      title: "Campeonato Paulista de Futevôlei",
      photographer: "Lucas Santos",
      date: "08/05/2025",
      location: "Praia Grande, SP",
      imageUrl: "/lovable-uploads/9d9dee7d-2850-451a-9469-91caabdf7f0c.png",
      category: "futevolei"
    },
    {
      title: "Torneio Nacional de Natação",
      photographer: "Mariana Costa",
      date: "05/05/2025",
      location: "Clube Pinheiros, São Paulo",
      imageUrl: "/lovable-uploads/cbb23aa4-bf2c-4d20-8c67-3e50fed861a3.png",
      category: "natacao"
    },
    {
      title: "Show Metallica Brasil Tour",
      photographer: "Paulo Rodrigues",
      date: "01/05/2025",
      location: "Allianz Parque, São Paulo",
      imageUrl: "/lovable-uploads/f2506d8b-d1d9-4850-84af-5b0a529302ba.png",
      category: "shows"
    },
    {
      title: "Circuito Estadual de Ciclismo",
      photographer: "Roberto Almeida",
      date: "28/04/2025",
      location: "Serra do Mar, PR",
      imageUrl: "/lovable-uploads/e4405c79-3173-4e9c-81d5-25973e61413d.png",
      category: "ciclismo"
    },
    {
      title: "Formatura Engenharia USP",
      photographer: "Juliana Pereira",
      date: "25/04/2025",
      location: "Centro de Convenções, São Paulo",
      imageUrl: "/lovable-uploads/cbb23aa4-bf2c-4d20-8c67-3e50fed861a3.png",
      category: "formaturas"
    },
    {
      title: "Campeonato Brasileiro de Futsal",
      photographer: "Rafael Oliveira",
      date: "20/04/2025",
      location: "Ginásio do Ibirapuera, São Paulo",
      imageUrl: "/lovable-uploads/5d0bfcd5-8f46-490c-8e19-f0153b1a59b3.png",
      category: "futsal"
    }
  ];

  // Available cities
  const cities = [
    "São Paulo", "Fortaleza", "Porto Alegre", "Rio de Janeiro", 
    "Brasília", "Belo Horizonte", "Salvador", "Curitiba", 
    "Goiânia", "Recife", "Natal"
  ];

  // Filter events by category
  const filteredEvents = selectedCategory === "all" 
    ? events 
    : events.filter(event => event.category === selectedCategory);

  return (
    <div className="min-h-screen flex flex-col bg-white font-sans">
      {LeadCaptureModalComponent}
      
      <header className="border-b border-gray-100 py-4">
        <div className="container max-w-7xl mx-auto px-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-[#323031]">TocFotosEventos</h1>
          <nav className="hidden md:flex space-x-6">
            <a href="#" className="text-gray-700 hover:text-[#FF5605] transition-colors">Início</a>
            <a href="#" className="text-gray-700 hover:text-[#FF5605] transition-colors">Eventos</a>
            <a href="#" className="text-gray-700 hover:text-[#FF5605] transition-colors">Fotógrafos</a>
            <a href="#" className="text-gray-700 hover:text-[#FF5605] transition-colors">Como funciona</a>
            <a href="#" className="text-gray-700 hover:text-[#FF5605] transition-colors">Contato</a>
          </nav>
          <Button variant="outline" className="border-[#3322FC] text-[#3322FC] hover:bg-[#3322FC] hover:text-white">
            Entrar
          </Button>
        </div>
      </header>

      <main>
        {/* Hero Search Section */}
        <section className="py-16 md:py-24">
          <div className="container max-w-7xl mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-[#323031]">
              Reviva o seu melhor momento. Encontre sua foto agora.
            </h1>
            <div className="max-w-3xl mx-auto space-y-4">
              <div className="flex flex-col sm:flex-row gap-2">
                <Input 
                  type="text" 
                  placeholder="Busque pelo nome, cidade, data ou evento…" 
                  className="flex-1 h-12"
                />
                <Button 
                  className="h-12 px-6 bg-[#FF5605] hover:bg-[#FF5605]/90 text-white flex items-center gap-2"
                >
                  <Search size={18} /> Buscar
                </Button>
              </div>
              
              <div className="overflow-x-auto pb-2">
                <div className="flex space-x-2 min-w-max">
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger className="h-10 w-[180px]">
                      <SelectValue placeholder="Categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas categorias</SelectItem>
                      <SelectItem value="beach-tennis">Beach Tennis</SelectItem>
                      <SelectItem value="corrida">Corrida</SelectItem>
                      <SelectItem value="ciclismo">Ciclismo</SelectItem>
                      <SelectItem value="futsal">Futsal</SelectItem>
                      <SelectItem value="natacao">Natação</SelectItem>
                      <SelectItem value="eventos">Eventos</SelectItem>
                      <SelectItem value="shows">Shows</SelectItem>
                      <SelectItem value="formaturas">Formaturas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Latest Events Section */}
        <section className="py-16 bg-gray-50">
          <div className="container max-w-7xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-semibold mb-8 text-center text-[#323031]">
              Últimos Eventos Registrados
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredEvents.map((event, index) => (
                <EventCard
                  key={index}
                  title={event.title}
                  photographer={event.photographer}
                  date={event.date}
                  location={event.location}
                  imageUrl={event.imageUrl}
                />
              ))}
            </div>
          </div>
        </section>
        
        {/* Photos All Over Brazil */}
        <section className="py-16">
          <div className="container max-w-7xl mx-auto px-4 text-center">
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              De Norte a Sul, capturamos <span className="text-[#3322FC] font-medium">histórias</span> reais. Compre suas fotos agora e compartilhe com orgulho.
            </p>
          </div>
        </section>
        
        {/* Cities Section */}
        <section className="py-16 bg-gray-50">
          <div className="container max-w-7xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-semibold mb-6 text-center text-[#323031]">
              Cidades Atendidas
            </h2>
            <div className="max-w-3xl mx-auto flex flex-wrap justify-center gap-2">
              {cities.map((city, index) => (
                <Badge key={index} variant="secondary" className="bg-gray-100 text-gray-800 hover:bg-gray-200">
                  {city}
                </Badge>
              ))}
              <Badge variant="secondary" className="bg-gray-100 text-gray-800 hover:bg-gray-200">
                e mais de 50 outras
              </Badge>
            </div>
          </div>
        </section>
        
        {/* Extra Resources */}
        <section className="py-16">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start">
                  <div className="text-[#3322FC] mr-3 mt-1">✔️</div>
                  <div>
                    <h3 className="font-semibold text-[#323031]">Fotógrafos com perfil verificado</h3>
                    <p className="text-gray-600">Todos os fotógrafos passam por um processo de validação para garantir qualidade.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="text-[#FF5605] mr-3 mt-1">⏳</div>
                  <div>
                    <h3 className="font-semibold text-[#323031]">Tecnologia de busca facial (em breve)</h3>
                    <p className="text-gray-600">Encontre suas fotos facilmente com nossa tecnologia de reconhecimento facial.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="text-[#3322FC] mr-3 mt-1">⚡</div>
                  <div>
                    <h3 className="font-semibold text-[#323031]">Compra com poucos cliques</h3>
                    <p className="text-gray-600">Processo simplificado para que você tenha suas fotos em segundos.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="text-[#FF5605] mr-3 mt-1">📥</div>
                  <div>
                    <h3 className="font-semibold text-[#323031]">Entrega digital em alta resolução</h3>
                    <p className="text-gray-600">Receba suas fotos em alta qualidade, prontas para impressão ou uso digital.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Final CTAs */}
        <section className="py-16 bg-gray-50">
          <div className="container max-w-7xl mx-auto px-4 text-center">
            <div className="space-y-4 md:space-y-0 md:space-x-4 flex flex-col md:flex-row justify-center">
              <Button 
                className="bg-[#FF5605] hover:bg-[#FF5605]/90 text-white px-8 py-6 rounded-full text-lg"
              >
                Procure agora
              </Button>
              <Button 
                onClick={() => openLeadCapture()}
                className="bg-[#3322FC] hover:bg-[#3322FC]/90 text-white px-8 py-6 rounded-full text-lg"
              >
                Seja um fotógrafo parceiro
              </Button>
            </div>
            <a href="#" className="block mt-6 text-xs text-gray-400 hover:text-gray-600 transition-colors">
              Política de Privacidade
            </a>
          </div>
        </section>
      </main>
      
      <TocFotosFooter />
    </div>
  );
};

export default TocFotosEventos02;
